// ─────────────────────────────────────────────
//  roomCampus — the campus DECISION for one sheet label, and the arrows the
//  sheet writes in the local dialect.
//
//  The rule table lives in roomCampusRules.js; the published room list is
//  partitioned in roomDefsCampus.js. This file only decides, and says how
//  confident it is and WHICH SOURCE ANSWERED.
//
//  ⚠️ IT NEVER GUESSES A HOSPITAL. A label two campuses can claim comes back
//  `contested` naming both; a label reached only by relaxing spelling comes
//  back `review`. A case on the wrong campus is a case at the wrong hospital.
//
//  Pure — no Firebase, no React, no network.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────

import {
  CAMPUS_RULES,
  SUPPLEMENTAL_ROOMS,
  BENCH_PATTERN,
  areaKey,
  foldArea,
  campusPrefixFor,
} from "./roomCampusRules";
import { crossCampusRiskFor } from "./roomDefsCampus";

// Re-exported so every consumer keeps one import site for the campus rule.
export {
  CAMPUS_RULES,
  SUPPLEMENTAL_ROOMS,
  BENCH_PATTERN,
  BENCH_FAMILY_OWNER,
  CAMPUS_FACILITY_IDS,
  areaKey,
  roomFamilyPattern,
  multiClaimedLabels,
  campusPrefixFor,
} from "./roomCampusRules";
export { crossCampusAreaIndex, roomDefsCampusIndex, crossCampusRiskFor } from "./roomDefsCampus";

// ──────────────────────────────────────
//  campusForRoomLabel
//
//  Decide WHICH HOSPITAL a verbatim sheet label belongs to — and say so out
//  loud when it cannot.
//
//  status:
//    "decided"   — exactly one rule claims the label as typed.
//    "bench"     — the campus-neutral TBD token.
//    "contested" — more than one campus can claim it. ⚠️ NEVER auto-resolved.
//                  `WC 5`, bare `TEE`, bare `CS`.
//    "review"    — no rule claims it as typed, but a punctuation/space-tolerant
//                  reading does. `WC-5`, `WCE1`. Reported, never auto-applied:
//                  routing it would be this module deciding a hospital from a
//                  typo.
//    "unclaimed" — nothing claims it. A section header, or a room nobody told
//                  us about.
//
//  @param {string} label the sheet's verbatim OR/Location cell
//  @param {object} [opts]
//    @param {Map} [opts.crossCampusAreas] crossCampusAreaIndex(...) — enables
//      the bare-dialect risk flag. Omit and `crossCampusRisk` is always null.
//  @returns {{label:string, facilityId:string|null, ruleId:string|null,
//             status:string, why:string, evidence:string,
//             candidates:Array<{facilityId:string, ruleId:string, how:string}>,
//             crossCampusRisk:null|{area:string, siblings:Array, why:string}}}
// ──────────────────────────────────────
// ──────────────────────────────────────
//  looserClaims — every campus that reaches this label once spelling is
//  relaxed. REPORTS ONLY; nothing here ever decides a hospital.
// ──────────────────────────────────────
function looserClaims(raw, defs, areas) {
  const key = areaKey(raw);
  const looser = [];

  // The published list, once spacing and punctuation are ignored. This is how
  // `WC-5` and `WCE1` are recognised — and precisely why the answer is a
  // review item rather than a routing decision: `WC 5` vs `WC5` proves that a
  // space in this vocabulary can BE the hospital.
  (defs ? defs.bySquash.get(key) || [] : []).forEach((e) =>
    looser.push({
      facilityId: e.facilityId,
      ruleId: e.ruleId,
      how: "room-defs-squashed",
      why: `Ignoring spacing and punctuation this reads as the published room "${e.name}".`,
      evidence: "Room Defs tab, Prelim Room Name column.",
    })
  );
  CAMPUS_RULES.forEach((r) => {
    if (r.pattern.test(key) && !looser.some((l) => l.facilityId === r.facilityId)) {
      looser.push({
        facilityId: r.facilityId,
        ruleId: r.id,
        how: "punctuation-tolerant",
        why: r.why,
        evidence: r.evidence,
      });
    }
  });

  // A bare area word published at another campus — `TEE`, `CS`. Today these
  // match nothing at all and the row is gone before the skip list exists.
  const siblings = areas ? areas.get(areaKey(foldArea(raw))) || [] : [];
  siblings.forEach((sib) => {
    if (!looser.some((l) => l.facilityId === sib.facilityId)) {
      looser.push({
        facilityId: sib.facilityId,
        ruleId: "cross-campus-area",
        how: "shared-area-word",
        why: `This area word is published at that campus as "${sib.name}".`,
        evidence: "Derived from the department's Room Defs tab.",
      });
    }
  });

  return looser;
}

export function campusForRoomLabel(label, opts = {}) {
  const raw = String(label ?? "").trim();
  const base = {
    label: raw,
    facilityId: null,
    ruleId: null,
    status: "unclaimed",
    source: "none",
    why: "",
    evidence: "",
    candidates: [],
    alsoClaimedBy: [],
    crossCampusRisk: null,
  };
  if (!raw) return { ...base, why: "empty label" };

  const defs = opts.roomDefs && opts.roomDefs.byName instanceof Map ? opts.roomDefs : null;
  const areas = (opts.crossCampusAreas instanceof Map && opts.crossCampusAreas) || defs?.areas || null;
  const risk = crossCampusRiskFor(raw, areas);

  // The bench token belongs to every campus and to none.
  if (BENCH_PATTERN.test(raw) || /\btbd\b/i.test(raw)) {
    return {
      ...base,
      status: "bench",
      source: "bench-token",
      why: "TBD is the campus-neutral unassigned bench, never a room.",
    };
  }

  // ── Rung 1: the department's published list, spacing HONOURED ────────────
  //  `WC 5` must not be read as `WC5` here; that single space is the campus.
  const published = defs ? defs.byName.get(raw.toLowerCase().replace(/\s+/g, " ")) : null;

  // ── Rung 2: our own campus rules, on the label exactly as typed ──────────
  const verbatim = CAMPUS_RULES.filter((r) => r.pattern.test(raw)).map((r) => ({
    facilityId: r.facilityId,
    ruleId: r.id,
    how: "verbatim",
    why: r.why,
    evidence: r.evidence,
  }));

  // ── Rung 3: rooms we know the published list omits ───────────────────────
  const supplemental = SUPPLEMENTAL_ROOMS.filter(
    (x) => areaKey(x.label) === areaKey(raw)
  ).map((x) => ({
    facilityId: x.facilityId,
    ruleId: `supplemental:${x.label}`,
    how: "known-missing-from-room-defs",
    why: x.why,
    evidence: x.evidence,
  }));

  const claims = [];
  if (published) {
    claims.push({
      facilityId: published.facilityId,
      ruleId: published.ruleId,
      how: "room-defs",
      why: `The department publishes "${published.name}" on its Room Defs tab.`,
      evidence: "Room Defs tab, Prelim Room Name column.",
    });
  }
  verbatim.forEach((c) => {
    if (!claims.some((x) => x.facilityId === c.facilityId)) claims.push(c);
  });
  supplemental.forEach((c) => {
    if (!claims.some((x) => x.facilityId === c.facilityId)) claims.push(c);
  });

  const distinct = new Set(claims.map((c) => c.facilityId));
  // ⚠️ EVEN A CONFIDENT ANSWER CARRIES ITS RIVAL. `WC 5` is Wesley Chapel by
  // the rule and Tampa's `WC5` by one deleted space — and today it resolves to
  // Wesley Chapel, finds no room there, and vanishes without ever mentioning
  // that the department publishes a room by that name at the OTHER hospital.
  const rivals = looserClaims(raw, defs, areas).filter((l) => !distinct.has(l.facilityId));
  if (distinct.size === 1) {
    const win = claims[0];
    return {
      ...base,
      alsoClaimedBy: rivals,
      facilityId: win.facilityId,
      ruleId: win.ruleId,
      status: "decided",
      source: published ? "room-defs" : win.how === "verbatim" ? "family-regex" : "supplemental",
      why: win.why,
      evidence: win.evidence,
      candidates: claims,
      crossCampusRisk: risk,
    };
  }
  if (distinct.size > 1) {
    return {
      ...base,
      alsoClaimedBy: rivals,
      status: "contested",
      source: "multiple",
      why:
        "More than one campus claims this label as typed. Deciding it here would " +
        "be a coin flip between two hospitals.",
      candidates: claims,
      crossCampusRisk: risk,
    };
  }

  const looser = looserClaims(raw, defs, areas);

  if (!looser.length) return { ...base, why: "No campus rule or published room claims this label." };

  const looseCampuses = new Set(looser.map((l) => l.facilityId));
  return {
    ...base,
    status: looseCampuses.size > 1 ? "contested" : "review",
    source: "near-miss",
    why:
      looseCampuses.size > 1
        ? `"${raw}" is claimed by ${looseCampuses.size} campuses once spelling is relaxed. ` +
          "Naming both is the only honest answer."
        : `Nothing claims "${raw}" as typed; relaxing spacing and punctuation reaches ` +
          "exactly one campus. Routing it automatically would be guessing a hospital " +
          "from a typo, so it is a review item.",
    candidates: looser,
    crossCampusRisk: risk,
  };
}

export function qualifyHint(hint, contextLabel) {
  const raw = String(hint ?? "").trim();
  if (!raw) return [];
  const out = [raw];
  const ctx = campusForRoomLabel(contextLabel);
  const fid = ctx.status === "decided" ? ctx.facilityId : null;
  if (!fid) return out;
  const { or, area } = campusPrefixFor(fid);
  // "OR 6" / "6" on a Carrollwood row means COR 6.
  const bareNumber = raw.replace(/^or\s*/i, "").trim();
  if (or && /^\d+$/.test(bareNumber)) {
    out.push(`${or}${bareNumber}`);
    // "OR 6" is an OR reference, never an area word — do not also offer
    // "CAR OR 6", which is not a room anywhere.
    return [...new Set(out)];
  }
  // "TEE" on a Wesley Chapel row means WC TEE.
  if (area && !/^(wc|car|cor|wor|tor)\b/i.test(raw)) out.push(`${area}${raw}`);
  return [...new Set(out)];
}
